<script lang="ts" setup>
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'
import { useHowTo } from '~/composables/useHowTo'
import { useStoreConfig } from '~/store/storeConfig'

const { hasAnyBlogPosts } = useStoreConfig()
const { isWhitelabel } = useTheme()
const { getSlug } = useHowTo()
const howToSlug = ref<string | null>(getSlug())
const { t } = useLang()
</script>

<template>
  <div class="faq-and-contacts w-full flex flex-wrap gap-7 justify-between">
    <div class="basic-info">
      <p class="font-bold pb-6">{{ t('footer.general-information') }}</p>
      <ul>
        <li>
          <NuxtLink :to="getLocalizedRoute(Routes.Contacts)">{{ t('contacts.contacts') }}</NuxtLink>
        </li>
        <li>
          <NuxtLink :to="getLocalizedRoute(Routes.Faq.Index)">{{ t('common.faq') }}</NuxtLink>
        </li>
        <li v-if="howToSlug">
          <NuxtLink :to="getLocalizedRoute(Routes.BlogSlug, { slug: howToSlug })">
            {{ t('common.how_to') }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink :to="getLocalizedRoute(Routes.About)">{{ t('common.about-us') }}</NuxtLink>
        </li>
        <li v-if="hasAnyBlogPosts">
          <NuxtLink :to="getLocalizedRoute(Routes.Blog)">{{ t('common.news') }}</NuxtLink>
        </li>
      </ul>
    </div>
    <div class="services">
      <p class="font-bold pb-6">{{ t('common.services') }}</p>
      <ul>
        <li>
          <NuxtLink :to="getLocalizedRoute(isWhitelabel ? Routes.Marketplace : Routes.Market)">
            {{ t('nav.dealzone') }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink :to="getLocalizedRoute(Routes.Quotes)">{{ t('nav.quotes') }}</NuxtLink>
        </li>
        <li>
          <NuxtLink :to="getLocalizedRoute(Routes.Pro)">{{ t('common.site-pro') }}</NuxtLink>
        </li>
      </ul>
    </div>
    <div class="contacts flex flex-col">
      <p class="font-bold pb-6">{{ t('common.contact-us') }}</p>
      <ul class="mb-6">
        <li>
          <i class="fa-solid fa-phone" />
          <a :href="`tel:${t('footer.store-phone')}`">{{ t('footer.store-phone') }}</a>
        </li>
        <li>
          <i class="fa-solid fa-envelope" />
          <a :href="`mailto:${t('footer.store-email')}`" target="_blank" v-html="t('footer.store-email')" />
        </li>
      </ul>
      <p class="font-bold pb-6">{{ t('support.page.label') }}</p>
      <ul>
        <li>
          <p class="font-normal text-sm">{{ t('support.page.hours') }}</p>
        </li>
        <li>
          <p class="font-normal text-sm">{{ t('support.page.disclaimer') }}</p>
        </li>
      </ul>
    </div>
    <PaymentDelivery />
  </div>
</template>

<style lang="postcss" scoped>
.faq-and-contacts {
  ul {
    li {
      @apply mb-6 last:mb-0 text-sm;

      a {
        @apply text-white focus:text-brand-primary hover:text-brand-primary;
      }
    }
  }
}
</style>

<style lang="postcss">
.faq-and-contacts {
  > * {
    max-width: 20rem;
    width: 100%;
  }

  hr {
    margin: 1.5rem 0;
    border-top: 1px solid theme('colors.gray7');
  }

  .contacts {
    li {
      display: flex;
      margin-bottom: 1rem;
      align-items: center;

      a {
        padding: 0.5rem 0;
        font-size: 0.875rem;
        line-height: 0.875rem;
      }
    }

    hr {
      max-width: 215px;
      margin: 0.75rem 0 1.5rem;
    }
  }

  .contacts {
    li {
      display: flex;
      align-items: center;
      color: theme('colors.white');
      font-weight: bold;

      i {
        padding-right: 10px;
        font-size: 1rem;
      }
    }
  }
}
</style>
